import React, { createContext, useContext, useEffect } from "react";
import { authService } from "../services/authService";
import { AuthMeResponse, LoginResponse } from "../models/auth/auth.response";
import { UserType } from "../models/base/UserType";
import { ApiResponse } from "../models/base/ApiResponse";
import { useNotification } from "../components/toster-notification/NotificationContext";
import { useNavigate } from "react-router-dom";
import { handleErrorNotification } from "../utils/errorNotificatioMessage";


type User = {
    id: string;
    type: UserType;
}

interface AuthContextType {
    user: User | null;
    logout: () => void;
    loading: boolean;
    login(email: string, password: string): Promise<UserType | null>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [user, setUser] = React.useState<User | null>(null);
    const [loading, setLoading] = React.useState<boolean>(true);
    const navigate = useNavigate();
    const { notify } = useNotification();

    useEffect(() => {
        fetchAuthMe();
    }, []);

    const fetchAuthMe = async () => {
        setLoading(true);
        try {
            const response: ApiResponse<AuthMeResponse> = await authService.authMe();

            if (!response.data) {
                notify("error", "Authentication Failed", response.message);
                setUser(null);
                return;
            }

            setUser({
                id: response.data.id,
                type: response.data.type
            });

        } catch (error: any) {
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        setLoading(true);
        try {
            const response: ApiResponse<string> = await authService.logout();

            notify("success", response.message);
            setUser(null);
            navigate("/login", { replace: true });

        } catch (error: any) {
            notify("error", "An error occurred during logout. Please try again.");
            handleErrorNotification(error, notify);
        } finally {
            setLoading(false);
        }
    };

    const login = async (email: string, password: string): Promise<UserType | null> => {
        setLoading(true);
        try {
            const response: ApiResponse<LoginResponse> = await authService.login(email, password);

            if (response.success === false || !response.data) {
                notify("error", "Login Failed", response.message);
                return null;
            }

            notify("success", "Login Successful", "You have successfully logged in.");

            setUser({
                id: response.data.userId,
                type: response.data.userType
            });

            return response.data.userType;

        } catch (error: any) {
            notify("error", "An error occurred during login. Please try again.");
            handleErrorNotification(error, notify);
            return null;
        } finally {
            setLoading(false);
        }
    };


    const value: AuthContextType = {
        user,
        logout,
        loading,
        login
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }

    return context;
}